import { SEO } from "@/components/SEO";
import { Helmet } from "react-helmet-async";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { NavLink } from "@/components/NavLink";
import { 
  Image,
  Check, 
  Phone,
  Calendar,
  ArrowRight,
  Eye,
  DollarSign,
  Star,
  Layers
} from "lucide-react";

const CANONICAL = "https://www.leafrushmarketing.com/landscaping-portfolio-website/";

const faqItems = [
  {
    question: "Why do landscapers need a portfolio website?",
    answer: "Landscaping is visual. Homeowners want to see your work before calling. A portfolio website showcases your projects, builds trust, and helps you close high-ticket jobs by proving you can deliver quality results."
  },
  {
    question: "What should a landscaping portfolio include?",
    answer: "Before/after photos organized by project type (patios, plantings, retaining walls, outdoor living), brief project descriptions, location (city/neighborhood), project scope, and any special features or challenges you solved."
  },
  {
    question: "How many projects should I show on my website?",
    answer: "Quality over quantity. 8–15 well-documented projects across your main service categories is enough to prove your capabilities. Update regularly as you complete new standout work."
  },
  {
    question: "Can a portfolio website help me get bigger projects?",
    answer: "Absolutely. High-ticket clients ($10K+ projects) research extensively before calling. A professional portfolio demonstrates your capabilities for larger, more complex work—not just basic maintenance."
  },
  {
    question: "Should I organize projects by service type or location?",
    answer: "Both. Primary organization by service type (patios, hardscape, plantings) lets visitors find relevant work. Adding location tags helps with local SEO and shows you work in their area."
  },
  {
    question: "Do I need professional photography?",
    answer: "Professional photos are ideal, but well-lit smartphone photos work too. The key is consistency: same angles for before/after, good lighting, clean job sites. We can advise on how to capture your work effectively."
  },
  {
    question: "How do I get before/after photos if I didn't take them?",
    answer: "Start now. Before every project, take photos from multiple angles. Train your crew to document 'before' as part of the job start checklist. You'll build a library quickly."
  },
  {
    question: "Should each project have its own page?",
    answer: "For flagship projects, yes. A dedicated project page with multiple photos, description, and details helps SEO and gives visitors a deeper look at your best work. Gallery pages work for smaller projects."
  },
  {
    question: "How does a portfolio website convert visitors to leads?",
    answer: "Clear CTAs on every page: 'Get a Quote for a Similar Project,' click-to-call, contact forms. After seeing your work, visitors should have an obvious next step to reach you."
  },
  {
    question: "Can you build a portfolio website with no existing photos?",
    answer: "Yes, but it's harder to convert visitors. We can launch with stock photos and a few of your best shots, then add projects as you complete and document them. Building the portfolio is an ongoing process."
  }
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${CANONICAL}#faq`,
  "mainEntity": faqItems.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": item.answer
    }
  }))
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${CANONICAL}#webpage`,
  "url": CANONICAL,
  "name": "Landscaping Portfolio Website: Showcase Projects That Win Jobs",
  "description": "A landscaping portfolio website showcases your best projects to win high-ticket clients. Learn how to structure galleries, before/afters, and project pages.",
  "isPartOf": {
    "@id": "https://www.leafrushmarketing.com/#website"
  },
  "about": {
    "@id": "https://www.leafrushmarketing.com/#organization"
  }
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://www.leafrushmarketing.com/#organization",
  "name": "Leaf Rush Marketing",
  "url": "https://www.leafrushmarketing.com",
  "logo": "https://www.leafrushmarketing.com/og-image.jpg"
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${CANONICAL}#service`,
  "name": "Landscaping Portfolio Website Design",
  "description": "Portfolio-focused website design for landscaping businesses. Showcase before/after projects, galleries, and case studies to win high-ticket clients.",
  "provider": {
    "@id": "https://www.leafrushmarketing.com/#organization"
  },
  "areaServed": {
    "@type": "Country",
    "name": "United States"
  },
  "serviceType": "Website Design"
};

const LandscapingPortfolioWebsite = () => {
  return (
    <>
      <SEO 
        title="Landscaping Portfolio Website: Showcase Projects That Win"
        description="A landscaping portfolio website showcases your best projects to win high-ticket clients. Learn how to structure galleries, before/afters, and project pages."
        canonical={CANONICAL}
        ogUrl={CANONICAL}
        ogImage="https://www.leafrushmarketing.com/og-image.jpg"
        keywords="landscaping portfolio website, landscaping project gallery website, landscaper portfolio website, before and after landscaping website"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <div className="pt-24">
        {/* Hero */}
        <section className="py-12 md:py-16 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <Badge className="mb-4">Portfolio & Galleries</Badge>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Landscaping Portfolio Website: Showcase Projects That Win High-Ticket Jobs
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Your best sales tool is proof. A portfolio website shows what you can do—patios, hardscape, outdoor living—so homeowners trust you with their project.
            </p>
          </div>
        </section>

        {/* Quick Answer Box */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="border-2 border-primary/20">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-2 mb-6">
                  <Image className="h-6 w-6 text-primary" />
                  <h2 className="text-2xl font-bold">Quick Answer: Why a Portfolio Website?</h2>
                </div>
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span><strong>Build trust instantly</strong> — Photos prove you deliver quality work</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span><strong>Win bigger projects</strong> — High-ticket clients research before calling</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span><strong>Differentiate from competitors</strong> — Show don't tell</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span><strong>Shorten the sales cycle</strong> — Visitors pre-sold on your capabilities</span>
                  </li>
                </ul>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a href="#get-started">
                    <Button size="lg" className="w-full sm:w-auto">
                      <Phone className="mr-2 h-4 w-4" />
                      Get Started
                    </Button>
                  </a>
                  <NavLink to="/landscaping-website-design/">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto">
                      See Full Design Features
                    </Button>
                  </NavLink>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* What Makes a Great Portfolio */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">What Makes a Great Landscaping Portfolio Website</h2>
            <p className="text-muted-foreground mb-8">
              A portfolio isn't just a photo dump. It's strategic content that converts browsers into buyers:
            </p>

            <div className="space-y-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Eye className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Before/After Transformations</h3>
                      <p className="text-muted-foreground">
                        Side-by-side comparisons that show the dramatic impact of your work. These are your most persuasive assets.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Layers className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Organized by Service Category</h3>
                      <p className="text-muted-foreground">
                        Patios, retaining walls, plantings, outdoor living, lighting—visitors can quickly find projects relevant to what they want.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <DollarSign className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Project Context & Scope</h3>
                      <p className="text-muted-foreground">
                        Brief descriptions that mention project size, challenges solved, location. Helps visitors imagine their own project.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Star className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Client Testimonials Paired with Photos</h3>
                      <p className="text-muted-foreground">
                        When a review mentions a specific project, pair it with the photos. Social proof + visual proof = trust.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* How Portfolio Drives Leads */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">How a Portfolio Website Drives High-Ticket Leads</h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg border">
                <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-semibold">Attracts Serious Buyers</h3>
                  <p className="text-sm text-muted-foreground">
                    Homeowners planning $5K–$50K+ projects research extensively. Your portfolio shows you handle projects at their level.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg border">
                <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-semibold">Pre-Sells Your Capabilities</h3>
                  <p className="text-sm text-muted-foreground">
                    Visitors who see your work and then call are already convinced. Less selling on the estimate, higher close rates.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg border">
                <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-semibold">Differentiates You From Competitors</h3>
                  <p className="text-sm text-muted-foreground">
                    Many landscapers have generic websites. A curated portfolio positions you as a premium provider, not a commodity.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg border">
                <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-semibold">Supports Local SEO</h3>
                  <p className="text-sm text-muted-foreground">
                    Project pages with location mentions help you rank for "landscaper in [city]" and related searches.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What We Include */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">What's Included in a Portfolio Website</h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Gallery pages organized by service type",
                "Before/after comparison layouts",
                "Featured project pages with details",
                "Location-tagged projects for local SEO",
                "Testimonials integrated with project photos",
                "Clear CTAs on every gallery page",
                "Mobile-optimized image loading",
                "Service pages linking to relevant projects",
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-2 p-3 bg-background rounded-lg border">
                  <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-sm text-muted-foreground mt-6">
              All included in our website plans starting at $200/month. See <NavLink to="/websites/" className="text-primary hover:underline">pricing details</NavLink>.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 bg-primary/5">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Showcase Your Best Work?</h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Book a 15-minute call. We'll discuss your projects, how to photograph them, and build a portfolio website that wins high-ticket jobs.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="#get-started">
                <Button size="lg">
                  <Calendar className="mr-2 h-4 w-4" />
                  Get Started
                </Button>
              </a>
              <NavLink to="/contact/">
                <Button size="lg" variant="outline">
                  Contact Us
                </Button>
              </NavLink>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl font-bold mb-6">Related Resources</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <NavLink to="/landscaping-website-design/" className="block p-4 border rounded-lg hover:border-primary transition-colors">
                <h3 className="font-semibold mb-1">Landscaping Website Design</h3>
                <p className="text-sm text-muted-foreground">Full details on what we build</p>
              </NavLink>
              <NavLink to="/landscaping-website-design-pricing/" className="block p-4 border rounded-lg hover:border-primary transition-colors">
                <h3 className="font-semibold mb-1">Landscaping Website Pricing</h3>
                <p className="text-sm text-muted-foreground">Detailed pricing breakdown</p>
              </NavLink>
              <NavLink to="/service-pages-that-rank/" className="block p-4 border rounded-lg hover:border-primary transition-colors">
                <h3 className="font-semibold mb-1">Service Pages That Rank</h3>
                <p className="text-sm text-muted-foreground">How to structure service pages for SEO</p>
              </NavLink>
              <NavLink to="/growth-add-ons/gbp/" className="block p-4 border rounded-lg hover:border-primary transition-colors">
                <h3 className="font-semibold mb-1">Google Business Profile</h3>
                <p className="text-sm text-muted-foreground">Boost local visibility with GBP</p>
              </NavLink>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-8">Landscaping Portfolio Website FAQ</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{item.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Calendly */}
        <section id="schedule-call" className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Book Your 15-Minute Fit Call</h2>
              <p className="text-muted-foreground">
                We'll discuss your landscaping business, your best projects, and how to build a portfolio that wins.
              </p>
            </div>
            <CalendlyEmbed />
          </div>
        </section>
      </div>
    </>
  );
};

export default LandscapingPortfolioWebsite;
